import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Badge, Button, Modal, ModalContent, styled, Text} from "@reside/ui";

import {SimpleFacilityModel} from "../../utils/types/facility";

import {FacilitySelect} from "./FacilitySelect";
import {WorkflowList} from "./WorkflowList";
import {WorkflowOption} from "./types";
import {navigateAfterWorkflowSelect} from "./navigation";
import {useFacilityWorkflowsQuery} from "./hooks/useFacilityWorkflowsQuery";
import {admissionOnlyWorkflowOption} from "./workflowMapping";
import {
  getSingleFacility,
  resolveActiveFacilityId,
} from "./resolveActiveFacilityId";

type Props = Readonly<{
  facilities: ReadonlyArray<SimpleFacilityModel>;
  isOpen: boolean;
  onRequestClose: () => void;
}>;

export const StartWorkflowModal = ({
  facilities,
  isOpen,
  onRequestClose,
}: Props) => {
  const hasSingleFacility = facilities.length === 1;
  const singleFacility = getSingleFacility(facilities);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(
    null,
  );
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowOption>(
    admissionOnlyWorkflowOption,
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedFacilityId(null);
      setSelectedWorkflow(admissionOnlyWorkflowOption());
    }
  }, [isOpen]);

  const activeFacilityId = resolveActiveFacilityId(
    facilities,
    selectedFacilityId,
  );

  useEffect(() => {
    setSelectedWorkflow(admissionOnlyWorkflowOption());
  }, [activeFacilityId]);

  const {isLoading: isLoadingWorkflows} = useFacilityWorkflowsQuery(
    activeFacilityId ?? undefined,
    undefined,
    {
      enabled: isOpen && Boolean(activeFacilityId),
    },
  );

  const activeFacility = useMemo(
    () => facilities.find(facility => facility.id === activeFacilityId),
    [activeFacilityId, facilities],
  );

  const handleStart = useCallback(() => {
    if (!activeFacilityId || !selectedWorkflow) {
      return;
    }

    onRequestClose();
    navigateAfterWorkflowSelect({
      facilityId: activeFacilityId,
      workflow: selectedWorkflow,
    });
  }, [activeFacilityId, onRequestClose, selectedWorkflow]);

  if (facilities.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      closeButtonPosition="right"
      title="Start a new workflow"
      subtitle="Pick a facility, then select a workflow to start."
      Content={StyledModalContent}
    >
      {({closeModal, Content, FooterButtons}) => (
        <>
          <Content data-test-id="startWorkflowModal">
            <StepHeader>
              <StepBadge color="lightBlue10">1</StepBadge>
              <StepBadgeText>FACILITY</StepBadgeText>
            </StepHeader>
            <FacilityHeader>
              {hasSingleFacility && singleFacility ? (
                <FacilityName>{singleFacility.name}</FacilityName>
              ) : (
                <FacilitySelect
                  facilities={facilities}
                  selectedFacilityId={selectedFacilityId}
                  onSelect={setSelectedFacilityId}
                />
              )}
            </FacilityHeader>
            <WorkflowSection>
              <StepHeader>
                <StepBadge color="lightBlue10">2</StepBadge>
                <StepBadgeText>WORKFLOW</StepBadgeText>
              </StepHeader>
              {activeFacility ? (
                <WorkflowList
                  facilityId={activeFacility.id}
                  selectedWorkflow={selectedWorkflow}
                  onSelect={setSelectedWorkflow}
                />
              ) : (
                <WorkflowPlaceholder data-test-id="startWorkflowPlaceholder">
                  Choose facility to display its workflows
                </WorkflowPlaceholder>
              )}
            </WorkflowSection>
          </Content>
          <FooterButtons>
            <Button
              color="primary"
              outline
              onClick={closeModal}
              testId="startWorkflowCancel"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={!activeFacilityId || isLoadingWorkflows}
              onClick={handleStart}
              testId="startWorkflowStart"
            >
              Start
            </Button>
          </FooterButtons>
        </>
      )}
    </Modal>
  );
};

const StyledModalContent = styled(ModalContent)`
  overflow: visible;
  color: ${({theme}) => theme.color.darkBlue100};
`;

const FacilityHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.space[3]}px;
  margin-bottom: ${({theme}) => theme.space[4]}px;
  margin-top: ${({theme}) => theme.space[4]}px;
`;

const WorkflowPlaceholder = styled(Text)`
  margin: 0;
  font-size: 16px;
  color: ${({theme}) => theme.color.gray100};
`;

const WorkflowSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme}) => theme.space[4]}px;
  margin-top: ${({theme}) => theme.space[6]}px;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.space[2]}px;
`;

const FacilityName = styled.span`
  min-width: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({theme}) => theme.color.textPrimary};
`;

const StepBadge = styled(Badge)`
  color: ${({theme}) => theme.color.darkBlue70};
  font-weight: 600;
  background-color: ${({theme}) => theme.color.lightBlue10};
  margin-left: ${({theme}) => theme.space[1]}px;
`;

const StepBadgeText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({theme}) => theme.color.darkBlue70};
`;
